/**
 * AgentOSStore — 全局 UI 黑板状态 (Zustand)
 *
 * Architecture 3.0 Phase 4, Layer 4:
 * 统一管理所有"面板开关 / 模态框 / 文件预览"等跨组件 UI 状态，
 * 彻底消除 AppLayout → Sidebar → SubComponent 的 Prop-Drilling 链。
 *
 * 设计原则：
 * - 只存储 UI 级别的开关/上下文数据（不存储业务数据如 sessions/messages）
 * - 业务数据仍由各自的 Hook（useCloudStorage, useEmailProviders 等）管理
 * - 所有状态都提供对应的 toggle/set action
 */

import { create } from 'zustand';
import { PreviewFile } from '../components/FilePreviewModal';

// ─── Panel / Drawer 状态 ───────────────────

interface PanelState {
  isRagPanelVisible: boolean;
  isLanguageSelectorOpen: boolean;
  showPrivacyConsent: boolean;
  isDocGeneratorOpen: boolean;
  isKnowledgeBaseOpen: boolean;
  isPricingOpen: boolean;
  isSettingsOpen: boolean;
  showOnboarding: boolean;
}

// ─── File Preview 状态 ─────────────────────

interface FilePreviewState {
  previewFile: PreviewFile | null;
}

// ─── Knowledge Upload 状态 ─────────────────

interface KnowledgeUploadState {
  knowledgeUploadState: {
    isUploading: boolean;
    fileName?: string;
    progress?: number;
    status?: 'uploading' | 'processing' | 'success' | 'error';
    error?: string;
  } | null;
}

// ─── 合并接口 ──────────────────────────────

export interface AgentOSState extends PanelState, FilePreviewState, KnowledgeUploadState {
  // Panel actions
  toggleRagPanel: () => void;
  setRagPanelVisible: (visible: boolean) => void;
  openLanguageSelector: () => void;
  closeLanguageSelector: () => void;
  openPrivacyConsent: () => void;
  closePrivacyConsent: () => void;
  openDocGenerator: () => void;
  closeDocGenerator: () => void;
  openKnowledgeBase: () => void;
  closeKnowledgeBase: () => void;
  openPricing: () => void;
  closePricing: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  openOnboarding: () => void;
  closeOnboarding: () => void;

  // File Preview actions
  setPreviewFile: (file: PreviewFile | null) => void;

  // Knowledge Upload actions
  setKnowledgeUploadState: (state: KnowledgeUploadState['knowledgeUploadState']) => void;
}

// ─── Store 实现 ────────────────────────────

export const useAgentOSStore = create<AgentOSState>((set) => ({
  // ── Panel 初始值 ──
  isRagPanelVisible: true,
  isLanguageSelectorOpen: false,
  showPrivacyConsent: false,
  isDocGeneratorOpen: false,
  isKnowledgeBaseOpen: false,
  isPricingOpen: false,
  isSettingsOpen: false,
  showOnboarding: false,

  // ── File Preview 初始值 ──
  previewFile: null,

  // ── Knowledge Upload 初始值 ──
  knowledgeUploadState: null,

  // ── Panel actions ──
  toggleRagPanel: () => set((s) => ({ isRagPanelVisible: !s.isRagPanelVisible })),
  setRagPanelVisible: (visible) => set({ isRagPanelVisible: visible }),
  openLanguageSelector: () => set({ isLanguageSelectorOpen: true }),
  closeLanguageSelector: () => set({ isLanguageSelectorOpen: false }),
  openPrivacyConsent: () => set({ showPrivacyConsent: true }),
  closePrivacyConsent: () => set({ showPrivacyConsent: false }),
  openDocGenerator: () => set({ isDocGeneratorOpen: true }),
  closeDocGenerator: () => set({ isDocGeneratorOpen: false }),
  openKnowledgeBase: () => set({ isKnowledgeBaseOpen: true }),
  closeKnowledgeBase: () => set({ isKnowledgeBaseOpen: false }),
  openPricing: () => set({ isPricingOpen: true }),
  closePricing: () => set({ isPricingOpen: false }),
  openSettings: () => set({ isSettingsOpen: true }),
  closeSettings: () => set({ isSettingsOpen: false }),
  openOnboarding: () => set({ showOnboarding: true }),
  closeOnboarding: () => set({ showOnboarding: false }),

  // ── File Preview action ──
  setPreviewFile: (file) => set({ previewFile: file }),

  // ── Knowledge Upload action ──
  setKnowledgeUploadState: (state) => set({ knowledgeUploadState: state }),
}));
